import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cmsService from '../../services/cmsService';
import apiClient from '../../services/apiClient';

// Async thunks for admin CMS operations
export const fetchAllPages = createAsyncThunk(
  'cmsAdmin/fetchAllPages',
  async ({ page = 1, limit = 10, search = '', sort_by = 'created_at', sort_order = 'DESC' }, { rejectWithValue }) => {
    try {
      // Call API with query parameters
      const response = await cmsService.getAllPages({
        page,
        limit,
        search,
        sort_by,
        sort_order
      });
      
      return {
        pages: response.data || [],
        pagination: response.pagination || {
          currentPage: page,
          totalPages: 1,
          totalRecords: 0,
          limit
        }
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch pages');
    }
  }
);

export const fetchPageById = createAsyncThunk(
  'cmsAdmin/fetchPageById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/cms/pages/${id}`);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch page');
    }
  }
);

export const createPage = createAsyncThunk(
  'cmsAdmin/createPage',
  async (pageData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/cms/pages', pageData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create page');
    }
  }
);

export const updatePage = createAsyncThunk(
  'cmsAdmin/updatePage',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/cms/pages/${id}`, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update page');
    }
  }
);

export const deletePage = createAsyncThunk(
  'cmsAdmin/deletePage',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/cms/pages/${id}`);
      return { id };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete page');
    }
  }
);

export const permanentDeletePage = createAsyncThunk(
  'cmsAdmin/permanentDeletePage',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/cms/pages/${id}/permanent`);
      return { id };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to permanently delete page');
    }
  }
);

export const restorePage = createAsyncThunk(
  'cmsAdmin/restorePage',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/cms/pages/${id}/restore`);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to restore page');
    }
  }
);

export const fetchPageByName = createAsyncThunk(
  'cmsAdmin/fetchPageByName',
  async (pageName, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/cms/pages/by-name?page_name=${encodeURIComponent(pageName)}`);
      return response.data || response;
    } catch (error) {
      // 404 means no existing content — not a hard error, just return null
      if (error?.status === 404 || error?.response?.status === 404) {
        return null;
      }
      return rejectWithValue(error.message || 'Failed to fetch page by name');
    }
  }
);

// Initial state
const initialState = {
  pages: [],
  currentPage: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  },
  loading: false,
  error: null,
  success: false
};

// CMS Admin slice
const cmsAdminSlice = createSlice({
  name: 'cmsAdmin',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetCurrentPage: (state) => {
      state.currentPage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Pages
      .addCase(fetchAllPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPages.fulfilled, (state, action) => {
        state.pages = action.payload.pages;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAllPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Page By ID
      .addCase(fetchPageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageById.fulfilled, (state, action) => {
        state.currentPage = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Page
      .addCase(createPage.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.pages.push(action.payload.data);
        state.loading = false;
        state.success = true;
      })
      .addCase(createPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update Page
      .addCase(updatePage.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updatePage.fulfilled, (state, action) => {
        const index = state.pages.findIndex(p => p.id === action.payload.data.id);
        if (index !== -1) {
          state.pages[index] = action.payload.data;
        }
        state.currentPage = action.payload.data;
        state.loading = false;
        state.success = true;
      })
      .addCase(updatePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete Page
      .addCase(deletePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.pages = state.pages.filter(p => p.id !== action.payload.id);
        state.loading = false;
        state.success = true;
      })
      .addCase(deletePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Permanent Delete Page
      .addCase(permanentDeletePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(permanentDeletePage.fulfilled, (state, action) => {
        state.pages = state.pages.filter(p => p.id !== action.payload.id);
        state.loading = false;
        state.success = true;
      })
      .addCase(permanentDeletePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Restore Page
      .addCase(restorePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restorePage.fulfilled, (state, action) => {
        const index = state.pages.findIndex(p => p.id === action.payload.data.id);
        if (index !== -1) {
          state.pages[index] = action.payload.data;
        } else {
          state.pages.push(action.payload.data);
        }
        state.loading = false;
        state.success = true;
      })
      .addCase(restorePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Page By Name (prefill on create)
      .addCase(fetchPageByName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageByName.fulfilled, (state, action) => {
        state.currentPage = action.payload; // null if 404 (no existing content)
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPageByName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, resetCurrentPage } = cmsAdminSlice.actions;
export default cmsAdminSlice.reducer;
